import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, TestTube, PartyPopper, Bug, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useAuth } from '../contexts/AuthContext';

import { useNoIndex } from '../hooks/useNoIndex';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccessPage = () => {
  useNoIndex();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [debugData, setDebugData] = useState(null);
  const [debugLoading, setDebugLoading] = useState(false);
  
  const conversationId = searchParams.get('conversationId');
  const paymentId = searchParams.get('paymentId');
  const token = searchParams.get('token');
  const isTest = searchParams.get('test') === 'true';

  // Fetch debug data from retrieve endpoint (admin only)
  useEffect(() => {
    const fetchDebugData = async () => {
      if (user?.role !== 'admin') return;
      
      const tokenToUse = token || localStorage.getItem('lastPaymentToken');
      if (!tokenToUse) return;
      
      setDebugLoading(true);
      try {
        const response = await fetch(`${API_URL}/api/payment/retrieve?token=${tokenToUse}`);
        const data = await response.json();
        setDebugData(data);
      } catch (err) {
        console.error('Debug fetch error:', err);
      } finally {
        setDebugLoading(false);
      }
    };
    
    fetchDebugData();
  }, [token, user]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            {isTest ? (
              <div className="relative">
                <CheckCircle className="w-20 h-20 text-amber-500" />
                <TestTube className="w-8 h-8 text-amber-600 absolute -bottom-1 -right-1 bg-background rounded-full p-1" />
              </div>
            ) : (
              <div className="relative">
                <CheckCircle className="w-20 h-20 text-green-500" />
                <PartyPopper className="w-8 h-8 text-green-600 absolute -bottom-1 -right-1 bg-background rounded-full p-1" />
              </div>
            )}
          </div>
          <CardTitle className="text-2xl">
            {isTest ? 'Test Ödemesi Başarılı!' : 'Ödeme Başarılı!'}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-6">
          {isTest ? (
            <div className="space-y-2">
              <p className="text-muted-foreground">
                Test ödemeniz başarıyla tamamlandı.
              </p>
              <p className="text-sm text-amber-600 font-medium">
                PRO aktif edilmedi (test modu)
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-muted-foreground">
                Ödemeniz başarıyla tamamlandı!
              </p>
              <p className="text-lg font-semibold text-primary">
                PRO üyeliğiniz aktif edildi
              </p>
            </div>
          )}
          
          <div className={`rounded-xl p-4 text-sm space-y-2 ${isTest ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-green-500/10 border border-green-500/20'}`}>
            {paymentId && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Ödeme ID:</span>
                <span className="font-mono text-xs">{paymentId}</span>
              </div>
            )}
            {conversationId && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">İşlem No:</span>
                <span className="font-mono text-xs">{conversationId}</span>
              </div>
            )}
            {isTest && (
              <p className="mt-3 text-xs text-amber-600 border-t border-amber-500/20 pt-2">
                İade için bu bilgileri saklayın veya iyzico panelinden iade yapabilirsiniz.
              </p>
            )}
          </div>
          
          {/* Admin Debug Box */}
          {user?.role === 'admin' && (
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 text-xs text-left">
              <div className="flex items-center gap-2 mb-2 text-amber-600 font-medium">
                <Bug className="w-4 h-4" />
                Admin Debug (iyzico Raw Response)
              </div>
              {debugLoading ? (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Yükleniyor...
                </div>
              ) : debugData ? (
                <div className="space-y-1 font-mono overflow-x-auto">
                  <div><span className="text-muted-foreground">paymentStatus:</span> <span className={debugData.key_fields?.paymentStatus === 'SUCCESS' ? 'text-green-500' : 'text-red-500'}>{debugData.key_fields?.paymentStatus || 'N/A'}</span></div>
                  <div><span className="text-muted-foreground">fraudStatus:</span> {debugData.key_fields?.fraudStatus || 'N/A'}</div>
                  <div><span className="text-muted-foreground">paymentId:</span> {debugData.key_fields?.paymentId || 'N/A'}</div>
                  <div><span className="text-muted-foreground">mdStatus:</span> <span className={debugData.key_fields?.mdStatus === 1 ? 'text-green-500' : 'text-amber-500'}>{debugData.key_fields?.mdStatus || 'N/A'}</span></div>
                  <div><span className="text-muted-foreground">price:</span> {debugData.key_fields?.price || 'N/A'}</div>
                  <div><span className="text-muted-foreground">paidPrice:</span> {debugData.key_fields?.paidPrice || 'N/A'}</div>
                </div>
              ) : (
                <div className="text-muted-foreground">Debug verisi yüklenemedi</div>
              )}
            </div>
          )}
          
          <div className="pt-4 space-y-3">
            {isTest ? (
              <Button 
                onClick={() => navigate('/dashboard/settings')} 
                className="w-full"
                variant="outline"
                data-testid="goto-settings-btn"
              >
                Ayarlara Dön
              </Button>
            ) : (
              <Button 
                onClick={() => navigate('/dashboard')} 
                className="w-full"
                data-testid="goto-dashboard-btn"
              >
                Dashboard'a Git
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccessPage;
